import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiList, apiPatch, apiPost } from '@/lib/api';

export type Trigger =
  | 'record_created'
  | 'record_updated'
  | 'field_changed'
  | 'stage_changed'
  | 'date_relative'
  | 'inactivity';

export const TRIGGER_LABELS: Record<Trigger, string> = {
  record_created: 'a record is created',
  record_updated: 'a record is changed',
  field_changed: 'a field changes',
  stage_changed: 'a record moves stage',
  date_relative: 'a date comes up',
  inactivity: 'nothing has happened for a while',
};

export const TRIGGER_HINTS: Record<Trigger, string> = {
  record_created: 'Fires once, the moment the record is saved for the first time.',
  record_updated: 'Fires on every save. Add conditions, or it will fire a great deal.',
  field_changed: 'Same moment as a change, but written for rules that watch one field.',
  stage_changed: 'Fires when a deal or lead is moved on its board. The Digital Pipeline screen sets these per stage.',
  date_relative: 'Checked hourly against a date on the record — a due date, a renewal.',
  inactivity: 'Checked hourly. Pair it with an "older than" condition on a date field.',
};

export const ENTITY_LABELS: Record<string, string> = {
  lead: 'Lead',
  deal: 'Deal',
  quote: 'Quote',
  company: 'Client',
  contact: 'Contact',
  invoice: 'Invoice',
  payment: 'Payment',
  project: 'Project',
  milestone: 'Milestone',
  work_item: 'Work item',
  ticket: 'Ticket',
  contract: 'Contract',
  subscription: 'Subscription',
};

export const OPERATOR_LABELS: Record<string, string> = {
  eq: 'is',
  neq: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  in: 'is one of',
  not_in: 'is not one of',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  changed: 'changed',
  changed_to: 'changed to',
  changed_from: 'changed from',
  older_than_days: 'is older than (days)',
  within_days: 'is within (days)',
};

export const ACTION_LABELS: Record<string, string> = {
  update_field: 'Set a field',
  assign_owner: 'Assign it to somebody',
  create_task: 'Create a task',
  create_ticket: 'Raise a ticket',
  send_email: 'Send an email',
  notify: 'Notify people',
  add_tag: 'Add a tag',
  move_stage: 'Move it to a stage',
  webhook: 'Call a webhook',
  send_message: 'Message the customer (chat/WhatsApp)',
  start_salesbot: 'Start a Salesbot',
};

export type Condition = { field: string; operator: string; value?: unknown };
export type ConditionGroup = {
  join: 'AND' | 'OR';
  conditions: Condition[];
  groups?: { join: 'AND' | 'OR'; conditions: Condition[] }[];
};
export type Action = { type: string; config: Record<string, unknown> };

export type WorkflowRule = {
  id: string;
  name: string;
  description: string | null;
  triggerType: Trigger;
  entityType: string;
  triggerConfig: Record<string, unknown> | null;
  conditions: ConditionGroup | null;
  actions: Action[];
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED';
  runCount: number;
  errorCount: number;
  lastRunAt: string | null;
  updatedAt: string;
};

export type WorkflowRun = {
  id: string;
  entityType: string;
  entityId: string;
  matched: boolean;
  succeeded: boolean;
  error: string | null;
  durationMs: number | null;
  ranAt: string;
  input: {
    conditions: { field: string; operator: string; expected: unknown; actual: unknown; passed: boolean }[];
  } | null;
  results: { type: string; ok: boolean; detail: string }[] | null;
  rule: { id: string; name: string };
};

export type Catalogue = {
  triggers: Trigger[];
  entities: string[];
  operators: string[];
  actions: string[];
  writableFields: Record<string, string[]>;
};

export function useWorkflowCatalogue() {
  return useQuery({
    queryKey: ['workflows', 'catalogue'],
    queryFn: () => apiGet<Catalogue>('/workflows/catalogue'),
    staleTime: 30 * 60 * 1000,
  });
}

export function useWorkflows(params: { entityType?: string; status?: string; q?: string } = {}) {
  return useQuery({
    queryKey: ['workflows', params],
    queryFn: () => apiGet<WorkflowRule[]>('/workflows', { params }),
  });
}

export function useWorkflow(id: string | undefined) {
  return useQuery({
    queryKey: ['workflows', 'rule', id],
    queryFn: () => apiGet<WorkflowRule>(`/workflows/${id}`),
    enabled: Boolean(id),
  });
}

export function useSaveWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id?: string;
      name: string;
      description?: string;
      triggerType: Trigger;
      entityType: string;
      triggerConfig?: Record<string, unknown>;
      conditions?: ConditionGroup | null;
      actions: Action[];
      status?: string;
    }) => apiPost<WorkflowRule>('/workflows', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflows'] }),
  });
}

export function useSetWorkflowStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'DRAFT' | 'ACTIVE' | 'PAUSED' }) =>
      apiPatch<{ id: string; status: string }>(`/workflows/${id}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflows'] }),
  });
}

export function useDeleteWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(`/workflows/${id}`);
      return { id };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflows'] }),
  });
}

export function useWorkflowRuns(params: { ruleId?: string; failed?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['workflows', 'runs', params],
    queryFn: () => apiList<WorkflowRun[]>('/workflows/runs', { params }),
  });
}

/** Evaluates a rule against a real record without performing its actions. */
export function useTestWorkflow() {
  return useMutation({
    mutationFn: (input: { ruleId: string; entityId: string }) =>
      apiPost<{
        matched: boolean;
        trace: { field: string; operator: string; expected: unknown; actual: unknown; passed: boolean }[];
        wouldRun: Action[];
      }>('/workflows/test', input),
  });
}
